import React from "react";
import { Badge } from "@/components/ui/badge";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { JsonFieldDisplay } from "@/components/shared/JsonFieldDisplay";
import { LinkifiedText } from "@/components/shared/LinkifiedText";
import { TagsFieldDisplay } from "@/components/shared/TagsFieldDisplay/TagsFieldDisplay";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";
import { formatNumberField } from "@/lib/utils/crm/format-number-field";
import { cn } from "@/lib/utils";
import type { JsonFieldValue } from "@/components/shared/types";
import type { SimpleFieldConfig, FieldValue } from "../types/index";
import { engagementStalenessBadgeClassNames } from "./cell-value-rendering-shared";

/**
 * Renders a due-date value with color-coded badge based on proximity to today.
 */
export function renderDueDateCell(value: FieldValue): React.ReactNode {
  if (!value) return <span className="text-gray-400">-</span>;

  let dueDate: Date;
  if (typeof value === "string") {
    const dateMatch = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, yearStr, monthStr, dayStr] = dateMatch;
      dueDate = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, parseInt(dayStr, 10));
    } else {
      dueDate = new Date(value);
    }
  } else {
    dueDate = value as Date;
  }

  const diffDays = Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  let variant: "default" | "destructive" | "secondary" = "default";
  let badgeClassName = "";

  if (diffDays < 0) {
    variant = "destructive";
    badgeClassName = "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
  } else if (diffDays === 0) {
    variant = "destructive";
    badgeClassName = "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
  } else if (diffDays <= 3) {
    variant = "secondary";
    badgeClassName = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  } else {
    variant = "default";
    badgeClassName = "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
  }

  return (
    <Badge variant={variant} className={cn("text-xs", badgeClassName)}>
      {formatDate(value as string)}
    </Badge>
  );
}

/**
 * Renders a datetime value, with optional engagement-staleness badge.
 */
export function renderDatetimeCell(value: FieldValue, fieldConfig: SimpleFieldConfig): React.ReactNode {
  if (fieldConfig.engagementStaleness) {
    if (value === null || value === undefined) {
      return (
        <Badge
          variant="secondary"
          className={cn("text-xs font-normal border-0", "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300")}
        >
          Never
        </Badge>
      );
    }
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return "-";
    const daysSince = (Date.now() - d.getTime()) / 86_400_000;
    return (
      <Badge
        variant="secondary"
        className={cn("text-xs font-normal border-0", engagementStalenessBadgeClassNames(daysSince))}
      >
        {formatDate(value as string)}
      </Badge>
    );
  }
  if (value === null || value === undefined) return "-";
  return formatDate(value as string);
}

/**
 * Renders a multi-user array or single user ID.
 */
export function renderMultiUserCell(value: FieldValue, isEditing: boolean): React.ReactNode {
  if (Array.isArray(value)) {
    return (
      <div className="flex gap-1 flex-wrap">
        {value.slice(0, 3).map((userItem, index) => {
          if (typeof userItem === "object" && userItem !== null && "id" in userItem) {
            const userRecord = userItem as Record<string, unknown>;
            return (
              <SmartUserDisplay
                key={index}
                userId={String(userRecord.id ?? "")}
                userData={{
                  id: typeof userRecord.id === "string" ? userRecord.id : undefined,
                  first_name:
                    typeof userRecord.first_name === "string" ? userRecord.first_name : null,
                  last_name:
                    typeof userRecord.last_name === "string" ? userRecord.last_name : null,
                  email: typeof userRecord.email === "string" ? userRecord.email : null,
                }}
                showIcon={true}
                className="inline-block"
                disabled={isEditing}
                showPopover={false}
              />
            );
          }
          return (
            <SmartUserDisplay
              key={index}
              userId={String(userItem)}
              showIcon={true}
              className="inline-block"
              disabled={isEditing}
              showPopover={false}
            />
          );
        })}
        {value.length > 3 && (
          <Badge variant="secondary" className="text-xs">
            +{value.length - 3}
          </Badge>
        )}
      </div>
    );
  }
  return (
    <SmartUserDisplay
      userId={String(value)}
      showIcon={true}
      className="inline-block"
      disabled={isEditing}
      showPopover={false}
    />
  );
}

/**
 * Handles all scalar field type rendering via a switch on fieldConfig.type.
 * Returns undefined when the type is not handled here (caller should fall through).
 */
export function renderScalarCell(
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  fieldId: string,
  isEditing: boolean
): React.ReactNode | undefined {
  switch (fieldConfig.type) {
    case "date":
      return formatDate(value as string);

    case "due-date":
      return renderDueDateCell(value);

    case "datetime":
      return renderDatetimeCell(value, fieldConfig);

    case "time": {
      if (value && typeof value === "string") {
        const timeValue = value.includes("T") ? value.split("T")[1] : value;
        return timeValue.substring(0, 5);
      }
      return String(value || "");
    }

    case "boolean":
      return value ? "Yes" : "No";

    case "number": {
      // Money/percent/grouped numbers when the field declares a format;
      // otherwise fall through to the caller's verbatim rendering so
      // identifier-ish numbers (ticket_number) keep printing exactly.
      const formatted = formatNumberField(value, fieldConfig);
      if (formatted !== null) {
        return <span className="tabular-nums">{formatted}</span>;
      }
      return undefined;
    }

    case "select": {
      const option = fieldConfig.options?.find((opt) => opt.value === value);
      return option ? option.label : String(value);
    }

    case "multi-user":
      return renderMultiUserCell(value, isEditing);

    case "json":
      return <JsonFieldDisplay value={value as JsonFieldValue} fieldId={fieldId} maxItems={3} />;

    case "tags":
      return <TagsFieldDisplay value={value} fieldId={fieldId} maxItems={3} />;

    case "text":
    case "textarea": {
      const str = String(value);
      // Linkify URLs so they're clickable; clicking a link opens it, clicking
      // elsewhere in an editable cell still opens the editor (anchor stops
      // propagation). Short text without URLs renders unchanged.
      return <LinkifiedText text={str} />;
    }

    default:
      return undefined;
  }
}
